import type { RuleCondition } from "@sauron/contracts"
import { Plus, X } from "lucide-react"
import { Button } from "@/components/ui/button.js"
import { Input } from "@/components/ui/input.js"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select.js"
import { money as formatMoney } from "@/lib/money.js"
import type { ConditionVocabulary } from "./condition-text.js"

/**
 * When a rule runs at all.
 *
 * **Built from the served vocabulary, never from a form written per field.** A
 * field added to `context` for a second question arrives here with its own
 * label and the operators its kind accepts — the same reasoning as
 * `ConfigControl` beside it, and the reason this screen has survived three
 * sources without an edit per source.
 *
 * The sentence under the list is the part that matters most: a rule skipped by
 * its conditions **allows**, and somebody setting a threshold at nine in the
 * evening should not have to infer that.
 */
export function Conditions({
  conditions,
  vocabulary,
  onChange,
}: {
  conditions: readonly RuleCondition[]
  vocabulary: ConditionVocabulary
  onChange: (next: RuleCondition[]) => void
}) {
  const replace = (index: number, next: RuleCondition) =>
    onChange(conditions.map((condition, i) => (i === index ? next : condition)))

  return (
    <div className="max-w-4xl">
      <div className="mb-1 flex items-baseline justify-between gap-3">
        {/* Styled as the editor's other section titles, because it is one. */}
        <h4 className="text-[13px] font-semibold text-foreground">Kiedy reguła w ogóle działa</h4>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...conditions, defaultCondition(vocabulary)])}
          disabled={vocabulary.fields.length === 0}
        >
          <Plus className="size-3.5" aria-hidden />
          Dodaj warunek
        </Button>
      </div>

      {vocabulary.fields.length === 0 ? (
        /*
         * The registry serves the vocabulary this is built from, so an empty
         * one means the panel is talking to an API that predates it — a build
         * mismatch, not a rule with nothing to offer. Saying which is the
         * difference between a control somebody reports as broken and one that
         * explains itself.
         */
        <p className="text-[13px] leading-5 text-muted-foreground">
          Ta wersja API nie podaje pól kontekstu, więc warunków nie da się tu ustawić.
        </p>
      ) : conditions.length === 0 ? (
        <p className="text-[13px] leading-5 text-muted-foreground">
          Bez warunków — reguła działa przy każdym zapytaniu.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {conditions.map((condition, index) => (
            <li
              // Conditions have no identity of their own and may repeat, so the
              // position is the only honest key.
              // biome-ignore lint/suspicious/noArrayIndexKey: a condition is its position
              key={index}
              className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card p-2"
            >
              <ConditionRow
                condition={condition}
                vocabulary={vocabulary}
                onChange={(next) => replace(index, next)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="ml-auto"
                aria-label="Usuń warunek"
                onClick={() => onChange(conditions.filter((_, i) => i !== index))}
              >
                <X className="size-3.5" aria-hidden />
              </Button>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 max-w-prose text-[13px] leading-5 text-muted-foreground">
        Wszystkie warunki muszą być spełnione. Reguła pominięta{" "}
        <strong className="font-medium text-foreground">nie blokuje</strong> — zapytanie, które ich
        nie spełnia, nie idzie przez to na przedpłatę, a jej źródło nie jest odpytywane.
      </p>
    </div>
  )
}

function ConditionRow({
  condition,
  vocabulary,
  onChange,
}: {
  condition: RuleCondition
  vocabulary: ConditionVocabulary
  onChange: (next: RuleCondition) => void
}) {
  const spec = vocabulary.fields.find((field) => field.field === condition.field)
  const operators = spec === undefined ? [] : (vocabulary.operators[spec.kind] ?? [])
  const comparison = condition.operator !== "present" && condition.operator !== "absent"

  return (
    <>
      <Select
        value={condition.field}
        onValueChange={(field) => onChange(forField(field, vocabulary))}
      >
        <SelectTrigger className="w-[13rem]" aria-label="Pole kontekstu">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {vocabulary.fields.map((field) => (
            <SelectItem key={field.field} value={field.field}>
              {field.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={condition.operator}
        onValueChange={(operator) => onChange(withOperator(condition, operator, vocabulary))}
      >
        <SelectTrigger className="w-[11rem]" aria-label="Operator">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {operators.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {comparison && <Operand condition={condition} onChange={onChange} />}

      {comparison && (
        <Select
          value={whenMissingOf(condition)}
          onValueChange={(whenMissing) => onChange({ ...condition, whenMissing } as RuleCondition)}
        >
          <SelectTrigger className="w-[13rem]" aria-label="Gdy zapytanie nie poda tego pola">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {vocabulary.missingPolicies.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </>
  )
}

/**
 * What a comparison does when the request omits the field. `in` narrows the
 * union: the presence variant carries no such setting, and never reaches here.
 */
function whenMissingOf(condition: RuleCondition): string {
  return "whenMissing" in condition ? (condition.whenMissing ?? "fail") : "fail"
}

/**
 * The operand, shaped by what the field holds rather than by the operator.
 *
 * Switched on `operator` rather than chained, because that is what
 * discriminates the union — the presence variant carries no operand at all.
 */
function Operand({
  condition,
  onChange,
}: {
  condition: RuleCondition
  onChange: (next: RuleCondition) => void
}) {
  switch (condition.operator) {
    case "present":
    case "absent":
      return null

    case "in":
    case "not_in":
      return (
        <Input
          className="w-[16rem]"
          aria-label="Wartości, po przecinku"
          value={condition.value.join(", ")}
          onChange={(event) =>
            onChange({
              ...condition,
              value: event.target.value
                .split(",")
                .map((item) => item.trim())
                .filter((item) => item !== ""),
            })
          }
        />
      )

    default: {
      const money = condition.value
      return (
        <>
          <Input
            type="number"
            min={0}
            step={100}
            className="w-[9rem] tabular-nums"
            aria-label="Kwota w groszach"
            value={money.amount}
            onChange={(event) =>
              onChange({
                ...condition,
                value: {
                  ...money,
                  amount: Math.max(0, Math.round(Number(event.target.value) || 0)),
                },
              })
            }
          />
          {/*
           * **The amount is minor units, and `30000` beside `PLN` reads as
           * thirty thousand złoty rather than three hundred.** The contract
           * stores grosze — integers, because no float belongs near money — so
           * the box cannot show złoty without rounding what somebody typed.
           * It says what it is counted in instead, the way a rule's bounded
           * parameter carries its `unit`, and the row ends with what the
           * figure is worth. A hundredfold misreading of the one number that
           * decides whether a paid source is asked is not a thing to leave to
           * an `aria-label`.
           */}
          <span className="shrink-0 text-[12.5px] text-muted-foreground">gr</span>
          <Input
            className="w-[5.5rem] uppercase"
            aria-label="Waluta"
            maxLength={3}
            value={money.currency}
            onChange={(event) =>
              onChange({
                ...condition,
                value: { ...money, currency: event.target.value.toUpperCase() },
              })
            }
          />
          {/* In the same words the rule's own summary uses, so the row and
              the list cannot describe one threshold differently. */}
          <span className="shrink-0 text-[12.5px] text-muted-foreground tabular-nums">
            = {formatMoney(money)}
          </span>
        </>
      )
    }
  }
}

function defaultCondition(vocabulary: ConditionVocabulary): RuleCondition {
  return forField(vocabulary.fields[0]?.field ?? "cartValue", vocabulary)
}

/**
 * Switching the field rebuilds the condition rather than keeping the operand.
 * A cart value and an order reference hold different kinds of thing, so
 * carrying one over would leave a shape the engine drops on the next publish.
 */
function forField(field: string, vocabulary: ConditionVocabulary): RuleCondition {
  const spec = vocabulary.fields.find((candidate) => candidate.field === field)
  if (spec?.kind === "text") {
    return { field, operator: "in", value: [] } as unknown as RuleCondition
  }
  return {
    field,
    operator: "gte",
    value: { amount: 0, currency: "PLN" },
  } as unknown as RuleCondition
}

/** Changing the operator can change what the operand has to be. */
function withOperator(
  condition: RuleCondition,
  operator: string,
  vocabulary: ConditionVocabulary,
): RuleCondition {
  if (operator === "present" || operator === "absent") {
    return { field: condition.field, operator } as RuleCondition
  }

  const rebuilt = forField(condition.field, vocabulary)
  const keepsOperand =
    (operator === "in" || operator === "not_in") ===
    (condition.operator === "in" || condition.operator === "not_in")

  return {
    ...(keepsOperand && "value" in condition ? condition : rebuilt),
    operator,
  } as RuleCondition
}
